/**
 * @typedef {import("moment").DurationInputArg1} DurationInputArg1
 * @typedef {import("moment").DurationInputArg2} DurationInputArg2
 * @typedef {import("moment").unitOfTime.StartOf} StartOf
 * @typedef {import("moment").DurationInputObject} DurationInputObject
 * * @typedef {import("moment").Moment} Moment
 */

/**
 * @typedef {Object} TimePeriodOptions
 * @property {DurationInputObject} [limit] - The limit for the time period.
 * @property {[DurationInputArg1, DurationInputArg2]} [range] - The range for the time period.
 * @property {StartOf} [startOf] - The start of the time period (e.g., 'year', 'month').
 * @property {boolean} [skipToday] - Whether to skip today in the time period.
 */

const DefaultTimePeriod = /** @type {const} */ ({
  hos: { limit: { months: 1 }, range: [6, "months"], startOf: false },
  "hos-logs": { limit: { months: 1 }, range: [1, "year"], startOf: "year" },
  "hos-report": { limit: { months: 6 }, range: [1, "year"], startOf: "year" },
  "hos-conformity": { limit: { months: 1 }, range: [1, "year"], startOf: "year" },
  "hos-register": { limit: { months: 12 }, range: [1, "year"], startOf: "year" },
  task: { range: [24, "months"], limit: { months: 24 } },
  dvir: { limit: { months: 1 }, range: [1, "year"], startOf: false },
  "dvir-short": { limit: { months: 1 }, range: [2, "months"], startOf: false },
  "dvir-mid": { limit: { months: 1 }, range: [6, "months"], startOf: false },
  maintenance: { limit: { months: 6 }, range: [1, "year"], startOf: "year" },
  report: { limit: { months: 6 }, range: [1, "year"], startOf: "year" },
  history: { limit: { months: 2 }, range: [2, "years"], startOf: "year" },
  usage: { limit: { months: 12 }, range: [1, "year"], startOf: "year", skipToday: true },
  odometer: { range: [24, "months"], limit: { months: 24 }, startOf: "year" },
  dtc: { range: [1, "year"], limit: { months: 1 } },

  default: { limit: { months: 1 }, range: [1, "year"], startOf: "year" },
});

/**
 * @typedef {keyof typeof DefaultTimePeriod} TimePeriodType
 */

class TimePeriod {
  /**
   * @template {TimePeriodType} T
   * @template {typeof DefaultTimePeriod[T]} U
   * Get the time period for a specific type.
   * @param {T} type - The type of time period (e.g., 'hos', 'historyPeriod', 'dvir').
   * @param {TimePeriodOptions} [timePeriod={}] - Optional custom time period options.
   * @param {boolean} [single=false] - Whether to return a single date range or an object with min and max dates.
   * @returns {{ dateLimit: DurationInputObject, minDate: Moment, maxDate: Moment }} - The time period details.
   */
  get(type, timePeriod = {}, single = false) {
    const period = Ingtech.webSettings.web[`timePeriod.${type}`] || {};
    const defaultPeriod = DefaultTimePeriod[type] || DefaultTimePeriod.default;

    const startOf = period.startOf ?? timePeriod.startOf ?? defaultPeriod.startOf;
    const minDate = moment().subtract(...(period.range || timePeriod.range || defaultPeriod.range));
    if (startOf) minDate.startOf(startOf);

    const maxDate = moment();
    if (period.skipToday ?? timePeriod.skipToday ?? defaultPeriod.skipToday)
      maxDate.subtract(1, "day");

    return {
      ...(!single ? { dateLimit: period.limit || timePeriod.limit || defaultPeriod.limit } : {}),

      minDate,
      maxDate,
    };
  }
}

const timePeriod = new TimePeriod();

module.exports = { timePeriod, TimePeriod, DefaultTimePeriod };

Ingtech.timePeriod = timePeriod;
